import {
  AssemblyGoverningValueDocument,
  PointOfInterest,
  PointOfInterestsDocument,
  useDeletePointOfInterestMutation,
} from '@/__generated__/graphql';
import { GenericDialog } from '@/components/shared/GenericDialog';
import {
  CANCEL_LABEL,
  DELETE_LABEL,
  DELETING_LABEL,
  NOMINATE_LABEL,
  NOMINATED_LABEL,
  POI_DELETE_DIALOG_CONTENT,
  POI_DELETE_DIALOG_TITLE,
  VIEW_IN_3D_LABEL,
  VIEW_IN_3D_TOOLTIP_LABEL,
} from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { MoreVert } from '@mui/icons-material';
import { Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useReviewIn3D } from '../../../hooks/useReviewIn3D';
import { actionsStyles, contentStyles, paperStyles, titleStyles } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { NominateDialog } from '@/components/Analysis/modules/pointOfInterest/aitInsights/Blisters/NominateDialog';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useFeatureFlag } from '@/hooks';

export const ActionMenu = ({ pointOfInterest }: { pointOfInterest: PointOfInterest }) => {
  const assemblyDetails = useAtomValue(state.selectedAssemblyDetails);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const [isNominateBlisterOpen, setIsNominateBlisterOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const { showSnackBar } = useSnackBarMessage({});
  const mainRef = useRef(null);
  const { handleViewIn3D } = useReviewIn3D({ pointOfInterest });

  const [deletePoi, { loading: deleteLoading }] = useDeletePointOfInterestMutation({
    variables: { input: { pointOfInterestId: pointOfInterest.id } },
    refetchQueries: [PointOfInterestsDocument, AssemblyGoverningValueDocument],
  });

  const handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorElement(mainRef.current ?? undefined);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeletePointOfInterest = async () => {
    try {
      await deletePoi();
      showSnackBar('POI deleted successfully', 'success');
    } catch {
      showSnackBar('Failed to delete POI. Please contact support', 'error');
    }
    handleDeleteDialogClose();
  };

  const handleNominateBlister = () => {
    setIsNominateBlisterOpen(true);
  };

  const closeNominateBlister = () => {
    setIsNominateBlisterOpen(false);
  };

  return (
    <>
      <IconButton
        ref={mainRef}
        onClick={handleMenuClick}
        size="small"
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVert sx={{ fontSize: '1.6rem' }} color="primary" />
      </IconButton>
      <Menu
        elevation={0}
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiMenuItem-root': {
            gap: '0.8rem',
          },
        }}
      >
        {isAITEngineer && pointOfInterest.blister && (
          <MenuItem disabled={!!pointOfInterest.isGoverning} onClick={handleNominateBlister}>
            <AbyssIcon iconName="governing-outline" size="md" />
            {pointOfInterest.isGoverning ? NOMINATED_LABEL : NOMINATE_LABEL}
          </MenuItem>
        )}
        <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="left" arrow>
          <MenuItem component="button" onClick={(event) => handleViewIn3D(event)}>
            <AbyssIcon iconName="cube-secondary" size="md" /> {VIEW_IN_3D_LABEL}
          </MenuItem>
        </Tooltip>
        {isAITEngineer && (
          <MenuItem onClick={handleDeleteClick}>
            <AbyssIcon iconName="trash" size="md" /> {DELETE_LABEL}
          </MenuItem>
        )}
      </Menu>

      {assemblyDetails && pointOfInterest && (
        <NominateDialog
          isOpen={isNominateBlisterOpen}
          onClose={closeNominateBlister}
          assemblyId={assemblyDetails?.id}
          assemblyTagName={assemblyDetails?.tagName}
          blisterId={pointOfInterest?.id}
          pipeSpec={assemblyDetails?.pipeSpec}
        />
      )}

      <GenericDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        title={POI_DELETE_DIALOG_TITLE}
        paperStyles={paperStyles}
        contentStyles={contentStyles}
        titleStyles={titleStyles}
        actionsStyles={actionsStyles}
        content={<Typography>{POI_DELETE_DIALOG_CONTENT}</Typography>}
        actions={[
          <Button onClick={handleDeleteDialogClose} size="medium">
            {CANCEL_LABEL}
          </Button>,
          <Button
            variant="contained"
            onClick={handleDeletePointOfInterest}
            size="medium"
            disabled={deleteLoading}
          >
            {deleteLoading ? DELETING_LABEL : DELETE_LABEL}
          </Button>,
        ]}
      />
    </>
  );
};
